import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Mail, FileSpreadsheet, CheckCircle, XCircle,
  Loader2, Send, RefreshCw, Copy, Info
} from 'lucide-react';
import { toast } from 'sonner';

const Integrations = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Super-admin only — block any non-super-admin who reaches this page via direct URL
  useEffect(() => {
    if (!isSuperAdmin()) {
      toast.error('Only Super Admin can manage integrations.');
      navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // WhatsApp test state - DISABLED
  // const [whatsappTest, setWhatsappTest] = useState({...});
  // const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Email test state
  const [emailTest, setEmailTest] = useState({ to_email: '', to_name: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Google Sheets service account email (read from backend config)
  const [serviceEmail, setServiceEmail] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/integrations/status');
      setStatus(response.data);
      
      // Try to get service email if sheets is configured
      if (response.data.google_sheets?.configured) {
        try {
          const emailRes = await api.get('/integrations/sheets/service-email');
          setServiceEmail(emailRes.data.service_email);
        } catch (e) {
          // Ignore
        }
      }
    } catch (error) {
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  // WhatsApp test function - DISABLED
  /*
  const testWhatsApp = async () => {
    // Function disabled
  };
  */

  const testEmail = async () => {
    if (!emailTest.to_email) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      await api.post('/integrations/email/test', {
        ...emailTest,
        to_name: emailTest.to_name || 'Test User',
        event_name: 'Test Event'
      });
      toast.success('Test email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingEmail(false);
    }
  };

  const copyServiceEmail = () => {
    navigator.clipboard.writeText(serviceEmail);
    toast.success('Service email copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900">Integrations</h1>
          <p className="text-slate-600 mt-1">Configure third-party integrations for notifications and data sync</p>
        </div>

        {/* Integration Cards */}
        <div className="space-y-6">
          
          {/* WhatsApp Integration - HIDDEN/DISABLED
          <Card className="p-6 opacity-50">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-slate-100">
                  <MessageSquare className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">WhatsApp (Interakt)</h2>
                  <p className="text-sm text-slate-500">Integration disabled - Contact support to enable</p>
                </div>
              </div>
              <span className="flex items-center gap-1 text-slate-400 text-sm">
                <XCircle className="w-4 h-4" /> Disabled
              </span>
            </div>
          </Card>
          */}

          {/* Email Integration */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${status?.email?.configured ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <Mail className={`w-6 h-6 ${status?.email?.configured ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Email (Brevo/Sendinblue)</h2>
                  <p className="text-sm text-slate-500">Send registration confirmation emails</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {status?.email?.configured ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" /> Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-slate-400 text-sm">
                    <XCircle className="w-4 h-4" /> Not Configured
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t">
              {!status?.email?.configured && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Email service is not configured. Contact your system administrator to set the Brevo API key in the server configuration.
                    </p>
                  </div>
                </div>
              )}
              {status?.email?.configured && (
                <>
                  <h3 className="text-sm font-medium text-slate-700 mb-3">Send Test Email</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label className="text-xs">Email Address</Label>
                      <Input
                        type="email"
                        placeholder="test@example.com"
                        value={emailTest.to_email}
                        onChange={(e) => setEmailTest({...emailTest, to_email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input
                        placeholder="Test User"
                        value={emailTest.to_name}
                        onChange={(e) => setEmailTest({...emailTest, to_name: e.target.value})}
                      />
                    </div>
                  </div>
                  <Button onClick={testEmail} disabled={sendingEmail} size="sm">
                    {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Test Email
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* Google Sheets Integration */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${status?.google_sheets?.configured ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <FileSpreadsheet className={`w-6 h-6 ${status?.google_sheets?.configured ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Google Sheets</h2>
                  <p className="text-sm text-slate-500">Auto-sync registrations to Google Sheets</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {status?.google_sheets?.configured ? (
                  <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                    <CheckCircle className="w-4 h-4" /> Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-slate-400 text-sm">
                    <XCircle className="w-4 h-4" /> Not Configured
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 pt-4 border-t">
              {!status?.google_sheets?.configured ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Google Sheets integration is not configured. Contact your system administrator to set up the service account credentials on the server.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <h3 className="text-sm font-medium text-slate-700 mb-3">Service Account Email</h3>
                  <p className="text-xs text-slate-500 mb-2">
                    If you want to sync to an existing Google Sheet, share it with this service email (Editor access):
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-100 px-3 py-2 rounded text-sm break-all">{serviceEmail || 'Loading…'}</code>
                    <Button variant="outline" size="sm" onClick={copyServiceEmail} disabled={!serviceEmail}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Or let the platform auto-create a new sheet per event via Settings → Integrations.
                  </p>
                </>
              )}
            </div>
          </Card>

        </div>

        {/* Refresh Button */}
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Status
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Integrations;
